import { trimToDecimals } from "utils";

import { CROP_YEAR_FIELD_LIMITS, EDITABLE_FIELDS, type EditableFieldKey } from "consts";

import type { SoilSightNonCropDefaults } from "models";

import { CropYear } from "pages/app/scenario-analysis/types/scenario";
import { growsCoverCrop } from "pages/app/scenario-analysis/utils/cropYearHelpers";
import { DetailedCalculationRow } from "pages/app/scenario-analysis/utils/detailedCalculations";

export const buildCropLabelResolver =
  (mainCropLabelLookup: Record<string, string>) =>
  (year: CropYear): string =>
    year.crop ? mainCropLabelLookup[year.crop] ?? "—" : "—";

/** Cover-crop-only fields are dropped from a rotation that grows none, rather than shown all locked. */
export const visibleEditableFields = (cycle: CropYear[]): (typeof EDITABLE_FIELDS)[number][] =>
  cycle.some(growsCoverCrop) ? [...EDITABLE_FIELDS] : EDITABLE_FIELDS.filter((field) => !("coverCropOnly" in field && field.coverCropOnly));

/**
 * Cover-crop seed cost the model charged each rotation slot, keyed by crop-year id — the prefill for the
 * `ccCost` input, so an untouched field shows the real figure rather than a blank or a generic default.
 */
export const buildCoverCropCostByYearId = (cycle: CropYear[], rows: DetailedCalculationRow[]): Record<string, number> => {
  const chargedBySlot = new Map<number, { cost: number; cropId: string | null }>();
  // The cover crop is charged with the harvest, so blank sowing rows never carry the figure.
  rows.forEach((row) => {
    if (row.cropId !== null && !chargedBySlot.has(row.rotationIndex)) chargedBySlot.set(row.rotationIndex, { cost: row.seedCostsCover, cropId: row.cropId });
  });

  const costByYearId: Record<string, number> = {};
  cycle.forEach((year, slotIndex) => {
    const charged = chargedBySlot.get(slotIndex);
    // Rows are from the last run while the cycle is live, so a reorder must not prefill another year's cost.
    if (!charged || charged.cropId !== year.crop) return;
    // Rounded to what the input can represent, so an untouched prefill never reads back as an edit.
    costByYearId[year.id] = trimToDecimals(charged.cost, CROP_YEAR_FIELD_LIMITS.ccCost.precision);
  });

  return costByYearId;
};

/** Supplies the value the model uses when the crop year holds no override. */
export type FieldFallbackResolver = (year: CropYear, field: EditableFieldKey) => number | null;

const coverCropMixDefaults = (defaults: SoilSightNonCropDefaults["coverCropDefaults"] | null | undefined, year: CropYear) =>
  growsCoverCrop(year) ? defaults?.[year.coverCropId] : undefined;

/** Stored override if the farmer set one, otherwise the value the model is actually using. */
export const effectiveFieldValue = (year: CropYear | undefined, field: EditableFieldKey, resolveFallback?: FieldFallbackResolver): number | null => {
  if (!year) return null;
  return year[field] ?? resolveFallback?.(year, field) ?? null;
};

/** Prefills from the country/crop defaults, so the table never opens blank. */
export const buildFieldFallbackResolver =
  (
    cropDefaultsByCropId: Record<string, Partial<Record<EditableFieldKey, number | null>>> | undefined,
    // Cover-crop seed cost depends on the mix and legume share, not the main crop, so it is keyed by row.
    coverCropCostByYearId?: Record<string, number>,
    // The legume share belongs to the mix, so it resolves from coverCropId. Crop defaults never carry it.
    coverCropDefaults?: SoilSightNonCropDefaults["coverCropDefaults"] | null
  ): FieldFallbackResolver =>
  (year, field) => {
    if (field === "ccCost") return coverCropCostByYearId?.[year.id] ?? null;
    if (field === "legumeShare") return coverCropMixDefaults(coverCropDefaults, year)?.legumeShare ?? null;
    return cropDefaultsByCropId?.[year.crop]?.[field] ?? null;
  };

const editableFieldsDiffer = (before: CropYear, after: CropYear): boolean => EDITABLE_FIELDS.some(({ key }) => (before[key] ?? null) !== (after[key] ?? null));

/** True once any editable field in the cycle has moved away from the snapshot taken when editing began. */
export const haveEditableFieldsChanged = (snapshot: CropYear[], cycle: CropYear[]): boolean =>
  cycle.some((year) => {
    const before = snapshot.find((candidate) => candidate.id === year.id);
    return before !== undefined && editableFieldsDiffer(before, year);
  });

/** Puts the snapshot's editable fields and updatedAt back on the cycle, leaving every other field and untouched year as it is. */
export const restoreEditableFields = (cycle: CropYear[], snapshot: CropYear[]): CropYear[] =>
  cycle.map((year) => {
    const before = snapshot.find((candidate) => candidate.id === year.id);
    if (!before || !editableFieldsDiffer(before, year)) return year;
    const restored = Object.fromEntries(EDITABLE_FIELDS.map(({ key }) => [key, before[key]])) as Partial<Pick<CropYear, EditableFieldKey>>;
    return { ...year, ...restored, updatedAt: before.updatedAt };
  });
